import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/UpgradePage.css';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { captureEvent } from '../_helpers/analytics.js';
import { NAME_PAINT_SURFACES } from '../_helpers/namePaint.js';
import { normalizeSkinShop } from '../_helpers/skinShop.js';
import config from '../config.js';
import {
  FOUNDING_LISTENER_SKIN,
  FREE_ITEMS,
  FREE_ROLE,
  INTERVALS,
  ROLE_KEY,
  ROLE_ORDER,
  addedItems,
  atPeriodEnd,
  changeKind,
  chargesNow,
  earnsFoundingListener,
  foundingOffer,
  hasPendingChange,
  opensModal,
  perMonthCents,
  planFor,
  planName,
  recommendedRole,
  roleItems,
  roleRank,
  standingOf,
  upgradeError,
  yearlySavingCents,
} from '../_helpers/planChanges.js';
import { useRoleSubscription } from '../hooks/useRoleSubscription.js';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import PaintedName from './partial/stream/PaintedName.jsx';
import PlanChangeModal from './partial/upgrade/PlanChangeModal.jsx';

/** The query Stripe Checkout comes back with: ?subscription=success|cancelled. */
const RETURN_PARAM = 'subscription';
/** After a successful Checkout, how often and how long to wait for the role. */
export const POLL_MS = 2000;
const POLL_LIMIT_MS = 30000;
const TOAST_MS = 4500;

/** Reads and removes ?subscription=… from the address bar; returns its value. */
const takeReturnParam = () => {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const value = url.searchParams.get(RETURN_PARAM);
  if (value == null) return null;
  url.searchParams.delete(RETURN_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return value;
};

const Check = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.2 4.2L19 7" />
  </svg>
);

const Star = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.8l2.4 5.6 6 .5-4.6 3.9 1.4 5.9L12 15.6l-5.2 3.1 1.4-5.9-4.6-3.9 6-.5z" />
  </svg>
);

/** The viewer's display name and initials, for the Founding Listener preview. */
const useOwnName = () => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [me, setMe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me`)
      .then((answer) => { if (!cancelled && answer && typeof answer === 'object') setMe(answer); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return me;
};

/** The Founding Listener paint's data from the skins catalog, so the card shows the real paint; null until read. */
const useFoundingPaint = (wanted) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [paint, setPaint] = useState(null);
  useEffect(() => {
    if (!wanted) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/skins`)
      .then((answer) => {
        if (cancelled) return;
        const item = normalizeSkinShop(answer).find((candidate) => candidate.skin?.id === FOUNDING_LISTENER_SKIN);
        if (item?.skin?.paint) setPaint(item.skin.paint);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [wanted]);
  return paint;
};

/** "Sam R." → "SR". */
const initialsOf = (name) => (name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

/** The Founding Listener card: the paint on the viewer's own name, the cutoff and the days left. */
const FoundingCard = ({ offer, me }) => {
  const { t, format } = useLocale();
  const paint = useFoundingPaint(Boolean(offer));
  const name = me?.displayName || t('shop.yourName');
  const initials = me?.initials || initialsOf(me?.displayName) || '';
  return (
    <aside className="upg-fl" aria-label={t('upgrade.founding.region')} data-testid="upgrade-founding">
      <div className="upg-fl-top">
        <span className="upg-fl-label">{t('upgrade.founding.label')}</span>
        <span className="upg-fl-rarity">{t('upgrade.founding.rarity')}</span>
      </div>
      <div className="upg-fl-chat" aria-hidden="true">
        <div className="upg-fl-row">
          <span className="upg-fl-av">{initials}</span>
          <div>
            {paint ? (
              <PaintedName name={name} paint={paint} surface={NAME_PAINT_SURFACES.chat} className="upg-fl-name" />
            ) : (
              <span className="upg-fl-name upg-fl-foil">{name}</span>
            )}
            <div className="upg-fl-msg">{t('upgrade.founding.sampleMessage')}</div>
          </div>
        </div>
        <div className="upg-fl-row upg-fl-row--dim">
          <span className="upg-fl-av upg-fl-av--other" />
          <div>
            <div className="upg-fl-plain">{t('upgrade.founding.otherName')}</div>
            <div className="upg-fl-msg">{t('upgrade.founding.otherMessage')}</div>
          </div>
        </div>
      </div>
      <p className="upg-fl-copy">{t('upgrade.founding.copy', { date: format.date(offer.until, 'short') })}</p>
      <p className="upg-fl-count" data-testid="upgrade-founding-days">
        {t('upgrade.founding.daysLeft', { count: offer.daysLeft, formattedCount: format.number(offer.daysLeft) })}
      </p>
    </aside>
  );
};

/** The catalog key of a card's button and hint for its kind. */
const cardAction = ({ t, format, kind, role, standing, planCents, isRec }) => {
  const day = (value) => format.date(value, 'short');
  const name = planName(t, role);
  switch (kind) {
    case 'current':
      if (hasPendingChange(standing) && role !== FREE_ROLE) {
        return {
          cta: t('upgrade.card.cta.keep', { plan: name }),
          hint: t('upgrade.card.hint.keep', { date: format.date(standing.periodEnd, 'monthDay') }),
          cls: 'upg-btn--ghost',
          undo: true,
        };
      }
      return {
        cta: t('upgrade.card.cta.current'),
        hint: role === FREE_ROLE || !standing.periodEnd || standing.grandfathered
          ? (role === FREE_ROLE ? t('upgrade.card.freeForever') : t('upgrade.card.hint.included'))
          : t('upgrade.card.hint.renews', { date: day(standing.periodEnd) }),
        cls: 'upg-btn--done',
        done: true,
      };
    case 'included':
      return { cta: t('upgrade.card.cta.included'), hint: t('upgrade.card.hint.included'), cls: 'upg-btn--done', done: true };
    case 'scheduled':
      return {
        cta: t('upgrade.card.cta.starts', { date: format.date(standing.periodEnd, 'monthDay') }),
        hint: t('upgrade.card.hint.scheduled'),
        cls: 'upg-btn--done',
        done: true,
      };
    case 'checkout':
      return {
        cta: t('upgrade.card.cta.get', { plan: name }),
        hint: t('upgrade.card.hint.checkout', { price: format.usd(planCents / 100, { cents: 'always' }) }),
        cls: isRec ? 'upg-btn--gold' : 'upg-btn--red',
      };
    case 'upgrade':
      return { cta: t('upgrade.card.cta.upgrade'), hint: t('upgrade.card.hint.upgrade'), cls: isRec ? 'upg-btn--gold' : 'upg-btn--red' };
    case 'switch_now':
      return { cta: t('upgrade.card.cta.switchYearly'), hint: t('upgrade.card.hint.switchNow'), cls: 'upg-btn--gold' };
    case 'switch_later':
      return { cta: t('upgrade.card.cta.switchMonthly'), hint: t('upgrade.card.hint.periodEnd', { date: day(standing.periodEnd) }), cls: 'upg-btn--ghost' };
    case 'cancel':
      return { cta: t('upgrade.card.cta.downgradeFree'), hint: t('upgrade.card.hint.periodEnd', { date: day(standing.periodEnd) }), cls: 'upg-btn--ghost' };
    default:
      return { cta: t('upgrade.card.cta.downgrade'), hint: t('upgrade.card.hint.periodEnd', { date: day(standing.periodEnd) }), cls: 'upg-btn--ghost' };
  }
};

/** One plan card: price for the chosen interval, its button, and what it adds over the plan below. */
const PlanCard = ({ role, interval, overview, standing, recRole, locked, onChoose, onUndo, busy }) => {
  const { t, format } = useLocale();
  const roleKey = ROLE_KEY[role];
  const plan = planFor(overview, role, interval);
  const kind = changeKind(standing, overview, role, interval);
  const isRec = role === recRole;
  const isCur = role === standing.role;
  const money = (cents) => format.usd(cents / 100, { cents: 'always' });
  const action = cardAction({ t, format, kind, role, standing, planCents: plan?.amountCents ?? 0, isRec });
  // Buying and changing need a plan Stripe can sell and an unlocked page;
  // undo and cancel never do (the backend allows both with the flag off).
  const unavailable = opensModal(kind) && kind !== 'cancel' && !plan?.available;
  const gated = !action.undo && kind !== 'cancel' && (locked || unavailable);
  const disabled = Boolean(action.done || busy || gated);

  let amt = format.usd(0, { cents: 'never' });
  let psub = t('upgrade.card.freeForever');
  let save = '';
  if (role !== FREE_ROLE) {
    const yearly = planFor(overview, role, 'year');
    if (interval === 'year') {
      amt = money(perMonthCents(plan));
      psub = plan ? t('upgrade.card.billedYearly', { price: money(plan.amountCents) }) : '';
      const saving = yearlySavingCents(overview, role);
      save = saving > 0 ? t('upgrade.card.save', { amount: money(saving) }) : '';
    } else {
      amt = money(plan?.amountCents ?? 0);
      psub = yearly ? t('upgrade.card.billedMonthly', { price: money(perMonthCents(yearly)) }) : '';
    }
  }

  const items = addedItems(overview, role);
  const founding = role !== FREE_ROLE && earnsFoundingListener(overview, role);
  const onClick = () => {
    if (action.undo) onUndo();
    else onChoose(role, interval, kind);
  };

  return (
    <li
      className={`upg-card${isRec ? ' upg-card--rec' : ''}${isCur ? ' upg-card--current' : ''}`}
      aria-label={t('upgrade.card.region', { plan: planName(t, role) })}
      data-testid={`upgrade-card-${role}`}
      data-kind={kind}
    >
      {(isRec || isCur) && (
        <div className={`upg-ribbon${isCur ? ' upg-ribbon--current' : ''}`}>
          {isCur ? t('upgrade.card.yourPlan') : t('upgrade.card.recommended')}
        </div>
      )}
      <div className={`upg-aud${roleRank(role) >= roleRank('performer') ? ' upg-aud--perform' : ''}`}>
        {t(`upgrade.plans.${roleKey}.audience`)}
      </div>
      <h3 className="upg-disp">{planName(t, role)}</h3>
      <p className="upg-tag">{t(`upgrade.plans.${roleKey}.tagline`)}</p>
      <div className="upg-price">
        <span className="upg-amt" data-testid="upgrade-card-price">{amt}</span>
        {role !== FREE_ROLE && <span className="upg-per">{t('upgrade.card.perMonth')}</span>}
      </div>
      <p className="upg-psub">{psub} {save && <b>{save}</b>}</p>
      <button type="button" className={`upg-btn ${action.cls}`} onClick={onClick} disabled={disabled} data-testid="upgrade-card-button">
        {action.cta}
      </button>
      <p className="upg-hint">{action.hint}</p>
      <div className="upg-inc">{t(`upgrade.card.includes.${roleKey}`)}</div>
      <ul className="upg-feat">
        {items.map((item) => (
          <li key={item}><Check /><span>{t(`upgrade.features.${item}`)}</span></li>
        ))}
        {founding && (
          <li><Check /><span className="upg-feat-gold">{t('upgrade.founding.feature')}</span></li>
        )}
      </ul>
    </li>
  );
};

/** The comparison table: every feature item against the four plans; scrolls in its box on phones. */
const ComparePlans = ({ overview, standing, interval }) => {
  const { t, format } = useLocale();
  const has = (role, item) => roleItems(overview, role).includes(item);
  const price = (role) => {
    const plan = planFor(overview, role, interval);
    if (!plan) return '';
    return t('upgrade.compare.price', {
      price: format.usd(plan.amountCents / 100, { cents: 'always' }),
      per: t(interval === 'year' ? 'upgrade.compare.perYear' : 'upgrade.compare.perMonth'),
    });
  };
  const offer = foundingOffer(overview);
  const groups = [
    { key: 'room', label: 'upgrade.compare.groupRoom', items: FREE_ITEMS, cell: () => true },
    { key: 'lp', label: 'upgrade.compare.groupListener', items: roleItems(overview, 'listener_premium') },
    { key: 'stage', label: 'upgrade.compare.groupStage', items: addedItems(overview, 'performer') },
    { key: 'pp', label: 'upgrade.compare.groupPerformer', items: addedItems(overview, 'performer_premium') },
  ].filter((group) => group.items.length > 0);
  const mark = (on) => (on
    ? <><span className="upg-ck" aria-hidden="true" /><span className="upg-sr">{t('upgrade.compare.included')}</span></>
    : <><span className="upg-no" aria-hidden="true" /><span className="upg-sr">{t('upgrade.compare.notIncluded')}</span></>);

  return (
    <div className="upg-cmp-box">
      <table className="upg-cmp">
        <thead>
          <tr>
            <th scope="col"><span className="upg-sr">{t('upgrade.compare.feature')}</span></th>
            {ROLE_ORDER.map((role) => (
              <th scope="col" key={role}>
                {role === standing.role && <span className="upg-cmp-yours">{t('upgrade.card.yourPlan')}</span>}
                {planName(t, role)}
                <small>{role === FREE_ROLE ? t('upgrade.compare.free') : price(role)}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              <tr className="upg-cmp-grp"><th scope="colgroup" colSpan={5}>{t(group.label)}</th></tr>
              {group.items.map((item) => (
                <tr key={item}>
                  <td>{t(`upgrade.features.${item}`)}</td>
                  {ROLE_ORDER.map((role) => <td key={role}>{mark(group.cell ? true : has(role, item))}</td>)}
                </tr>
              ))}
              {group.key === 'lp' && offer && (
                <tr>
                  <td>
                    {t('upgrade.founding.feature')}
                    <span className="upg-soon">{t('upgrade.compare.foundingUntil', { date: format.date(offer.until, 'short') })}</span>
                  </td>
                  {ROLE_ORDER.map((role) => <td key={role}>{mark(earnsFoundingListener(overview, role))}</td>)}
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** The billing FAQ; the Founding Listener answer only while the offer is open. */
const UpgradeFaq = ({ offer }) => {
  const { t, format } = useLocale();
  const entries = [
    ['midMonth', {}],
    ['downgrade', {}],
    ['interval', {}],
    ['performer', {}],
    ...(offer ? [['founding', { date: format.date(offer.until, 'short') }]] : []),
    ['cancel', {}],
  ];
  return (
    <div className="upg-faq">
      {entries.map(([key, params], index) => (
        <details key={key} open={index === 0}>
          <summary>{t(`upgrade.faq.${key}Q`)}</summary>
          <p>{t(`upgrade.faq.${key}A`, params)}</p>
        </details>
      ))}
    </div>
  );
};

/**
 * The Upgrade page (ENG-892) at `/upgrade`, the "A · Main Stage" design and
 * the shared subscription page of ENG-756 / ENG-762: the header's UPGRADE
 * pill opens it. Four plans (Listener free, Listener Premium, Performer,
 * Performer Premium) with prices, features and the Founding Listener offer
 * from `GET /me/subscription`; nothing about a plan is hard-coded.
 *
 * Each card's button follows `changeKind` (src/_helpers/planChanges.js), the
 * backend's rules: no subscription goes to Stripe Checkout (back to
 * `/upgrade?subscription=…`); an upgrade or monthly-to-yearly switch is
 * priced by the preview and charged with its proration date; a downgrade,
 * yearly-to-monthly switch or cancel waits for the end of the period and can
 * be undone from the standing strip or the current plan's card. `?plan=`
 * preselects the recommended plan, `?interval=` the billing switch.
 *
 * Buying and changing are disabled while the Stripe flag is off or a first
 * bank payment is still processing; undo stays available. The page shows no
 * broadcast, so its dialog and toast cover none.
 */
/** `pollMs` is how often a Checkout return re-reads the subscription (tests shorten it). */
const UpgradePage = ({ pollMs = POLL_MS } = {}) => {
  const { t, format } = useLocale();
  const [searchParams] = useSearchParams();
  const sub = useRoleSubscription();
  const me = useOwnName();
  const preselected = ROLE_KEY[searchParams.get('plan')] ? searchParams.get('plan') : null;
  const askedInterval = INTERVALS.includes(searchParams.get('interval')) ? searchParams.get('interval') : null;

  const [interval, setInterval_] = useState(null);
  const [choice, setChoice] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [undoError, setUndoError] = useState(null);
  const [returned] = useState(takeReturnParam);
  const [awaiting, setAwaiting] = useState(returned === 'success' ? 'waiting' : null);
  const toastTimer = useRef(null);
  const viewed = useRef(false);

  const overview = sub.overview;
  const standing = useMemo(() => standingOf(overview), [overview]);
  const shownInterval = interval || askedInterval || standing.interval || 'year';
  const recRole = overview ? recommendedRole(standing, overview, shownInterval, preselected) : null;
  const offer = foundingOffer(overview);
  const locked = Boolean(overview && (!overview.stripeEnabled || standing.paymentPending));

  const showToast = useCallback((text) => {
    clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (returned === 'cancelled') showToast(t('upgrade.toast.checkoutCancelled'));
    // Once, for the return Stripe sent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewed.current || sub.status !== 'ready') return;
    viewed.current = true;
    captureEvent('upgrade_page_viewed', { current_role: standing.role, preselected_plan: preselected });
  }, [sub.status, standing.role, preselected]);

  // Back from a paid Checkout: wait for the webhook to grant the role.
  const granted = overview?.subscription?.grantedRole ?? null;
  const { reload } = sub;
  useEffect(() => {
    if (awaiting !== 'waiting') return undefined;
    if (granted) {
      setAwaiting(null);
      showToast(t('upgrade.toast.welcome', { plan: planName(t, granted) }));
      return undefined;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started >= POLL_LIMIT_MS) {
        clearInterval(timer);
        setAwaiting('slow');
        return;
      }
      reload().catch(() => {});
    }, pollMs);
    return () => clearInterval(timer);
  }, [awaiting, granted, reload, showToast, t, pollMs]);

  const choose = useCallback((role, chosen, kind) => {
    captureEvent('upgrade_plan_selected', { role, interval: chosen, kind, current_role: standing.role });
    setChoice({ role, interval: chosen, kind });
  }, [standing.role]);

  const undo = useCallback(async () => {
    if (!standing.subscription) return;
    setBusy(true);
    setUndoError(null);
    const role = standing.role;
    const current = standing.interval;
    try {
      await sub.change(role, current);
      captureEvent('upgrade_change_undone', { role, interval: current });
      showToast(t('upgrade.toast.undone', { plan: planName(t, role) }));
    } catch (err) {
      setUndoError(err);
    } finally {
      setBusy(false);
    }
  }, [standing, sub, showToast, t]);

  const loadPreview = useCallback(() => {
    if (!choice) return Promise.resolve(null);
    return sub.preview(choice.role, choice.interval, {
      replaceScheduled: chargesNow(choice.kind) && hasPendingChange(standing),
    });
  }, [choice, standing, sub]);

  const confirm = useCallback(async ({ prorationDate }) => {
    const { role, interval: chosen, kind } = choice;
    const event = { role, interval: chosen, kind, current_role: standing.role };
    const periodEnd = standing.periodEnd;
    try {
      if (kind === 'checkout') {
        captureEvent('upgrade_checkout_started', event);
        await sub.checkout(role, chosen);
        return;
      }
      if (kind === 'cancel') await sub.cancel();
      else {
        await sub.change(role, chosen, {
          prorationDate: chargesNow(kind) ? prorationDate : null,
          replaceScheduled: chargesNow(kind) && hasPendingChange(standing),
        });
      }
      captureEvent('upgrade_change_confirmed', { ...event, effective: atPeriodEnd(kind) ? 'period_end' : 'now' });
      setChoice(null);
      if (atPeriodEnd(kind)) showToast(t('upgrade.toast.scheduled', { date: format.date(periodEnd, 'short') }));
      else if (kind === 'switch_now') showToast(t('upgrade.toast.yearly'));
      else showToast(t('upgrade.toast.welcome', { plan: planName(t, role) }));
    } catch (err) {
      captureEvent('upgrade_change_failed', { ...event, code: err?.code || null });
      throw err;
    }
  }, [choice, standing, sub, showToast, t, format]);

  const heroAct = () => {
    if (!recRole) return;
    choose(recRole, shownInterval, changeKind(standing, overview, recRole, shownInterval));
  };
  const recPlan = recRole ? planFor(overview, recRole, shownInterval) : null;
  const heroCta = recRole
    ? t(standing.role === FREE_ROLE ? 'upgrade.hero.ctaGet' : 'upgrade.hero.ctaUpgrade', {
      plan: planName(t, recRole),
      price: format.usd(perMonthCents(recPlan) / 100, { cents: 'always' }),
    })
    : '';
  const heroDisabled = locked || !recPlan?.available;

  let content;
  if (sub.status === 'loading') {
    content = <p className="upg-state" role="status">{t('upgrade.loading')}</p>;
  } else if (sub.status === 'error') {
    content = (
      <div className="upg-state" role="alert">
        <p>{t('upgrade.loadError')}</p>
        <button type="button" className="upg-mini" onClick={() => sub.reload().catch(() => {})}>{t('common.actions.retry')}</button>
      </div>
    );
  } else {
    const roleKey = ROLE_KEY[standing.role];
    const day = (value) => format.date(value, 'short');
    let statusLine;
    if (standing.role === FREE_ROLE) statusLine = t('upgrade.status.free');
    else if (standing.grandfathered) statusLine = t('upgrade.status.grandfathered', { plan: planName(t, standing.role) });
    else if (!standing.periodEnd) {
      statusLine = t('upgrade.status.current', { plan: planName(t, standing.role), interval: t(`upgrade.interval.${standing.interval}`) });
    } else {
      statusLine = t(hasPendingChange(standing) ? 'upgrade.status.activeUntil' : 'upgrade.status.renews', {
        plan: planName(t, standing.role),
        interval: t(`upgrade.interval.${standing.interval}`),
        date: day(standing.periodEnd),
      });
    }
    let schedLine = null;
    if (standing.cancelAtPeriodEnd) schedLine = t('upgrade.status.cancelling', { date: day(standing.periodEnd) });
    else if (standing.scheduledChange) {
      schedLine = t('upgrade.status.switching', {
        plan: planName(t, standing.scheduledChange.role),
        interval: t(`upgrade.interval.${standing.scheduledChange.interval === 'year' ? 'year' : 'month'}`),
        date: day(standing.scheduledChange.effectiveAt || standing.periodEnd),
      });
    }
    let lockNote = null;
    if (awaiting === 'waiting' || standing.awaitingRole) lockNote = t('upgrade.status.awaiting');
    if (awaiting === 'slow') lockNote = t('upgrade.status.awaitingSlow');
    if (standing.paymentPending) lockNote = t('upgrade.status.paymentPending');
    else if (overview && !overview.stripeEnabled) lockNote = t('upgrade.status.stripeOff');
    const undoWords = undoError ? upgradeError(undoError) : null;
    const undoMessage = undoWords ? (undoWords.key ? t(undoWords.key) : undoWords.text) : null;

    content = (
      <>
        <section className="upg-hero" aria-labelledby="upg-hero-title">
          <div>
            <span className="upg-eyebrow"><Star />{t('upgrade.hero.eyebrow')}</span>
            <h1 id="upg-hero-title" className="upg-disp">{t('upgrade.hero.title')}</h1>
            <p className="upg-lead">{t('upgrade.hero.lead')}</p>
            <div className="upg-hero-ctas">
              {recRole ? (
                <button type="button" className="upg-btn upg-btn--gold" onClick={heroAct} disabled={heroDisabled} data-testid="upgrade-hero-cta">{heroCta}</button>
              ) : (
                <a href="#plans" className="upg-btn upg-btn--gold" data-testid="upgrade-hero-cta">{t('upgrade.hero.seeYourPlan')}</a>
              )}
              <a href="#compare" className="upg-text-link">{t('upgrade.hero.compare')}</a>
            </div>
            <ul className="upg-proof">
              <li><Check />{t('upgrade.hero.proofProrated')}</li>
              <li><Check />{t('upgrade.hero.proofCancel')}</li>
            </ul>
          </div>
          {offer && <FoundingCard offer={offer} me={me} />}
        </section>

        <section id="plans" className="upg-sec" aria-labelledby="upg-plans-title">
          <div className="upg-sec-head">
            <div>
              <div className="upg-kicker">{t('upgrade.section.plansKicker')}</div>
              <h2 id="upg-plans-title" className="upg-disp">{t('upgrade.section.plansTitle')}</h2>
              <p className="upg-sub">{t('upgrade.section.plansSub')}</p>
            </div>
            <div className="upg-toggle" role="group" aria-label={t('upgrade.billing.label')}>
              <button type="button" aria-pressed={shownInterval === 'month'} onClick={() => setInterval_('month')}>
                {t('upgrade.billing.monthly')}
              </button>
              <button type="button" aria-pressed={shownInterval === 'year'} onClick={() => setInterval_('year')}>
                {t('upgrade.billing.yearly')} <span className="upg-save-pill">{t('upgrade.billing.monthsFree')}</span>
              </button>
            </div>
          </div>

          <div className="upg-status" data-testid="upgrade-status">
            <div className="upg-status-l"><span className="upg-status-dot" aria-hidden="true" /><span>{statusLine}</span></div>
            {schedLine && (
              <div className="upg-sched">
                <span>{schedLine}</span>
                <button type="button" className="upg-mini" onClick={undo} disabled={busy} data-testid="upgrade-undo">{t('upgrade.status.undo')}</button>
              </div>
            )}
            {lockNote && <p className="upg-status-note" role="status">{lockNote}</p>}
            {undoMessage && <p className="upg-status-note" role="alert">{undoMessage}</p>}
          </div>

          <ul className="upg-grid">
            {ROLE_ORDER.map((role) => (
              <PlanCard
                key={role}
                role={role}
                interval={shownInterval}
                overview={overview}
                standing={standing}
                recRole={recRole}
                locked={locked}
                busy={busy}
                onChoose={choose}
                onUndo={undo}
              />
            ))}
          </ul>

          <div className="upg-assure">
            {['upgrade', 'downgrade', 'undo'].map((key) => (
              <div className="upg-as" key={key}>
                <span className="upg-as-ic"><Check size={20} /></span>
                <div>
                  <h3>{t(`upgrade.assure.${key}Title`)}</h3>
                  <p>{t(`upgrade.assure.${key}Body`)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="compare" className="upg-sec" aria-labelledby="upg-compare-title">
          <div className="upg-kicker">{t('upgrade.section.compareKicker')}</div>
          <h2 id="upg-compare-title" className="upg-disp">{t('upgrade.section.compareTitle')}</h2>
          <ComparePlans overview={overview} standing={standing} interval={shownInterval} />
        </section>

        <section className="upg-sec" aria-labelledby="upg-faq-title">
          <div className="upg-kicker">{t('upgrade.section.faqKicker')}</div>
          <h2 id="upg-faq-title" className="upg-disp">{t('upgrade.section.faqTitle')}</h2>
          <UpgradeFaq offer={offer} />
        </section>

        <section className="upg-close" aria-labelledby="upg-close-title">
          <div>
            <h2 id="upg-close-title" className="upg-disp">{t(`upgrade.closing.${roleKey}Title`)}</h2>
            <p>{t(`upgrade.closing.${roleKey}Sub`)}</p>
          </div>
          {recRole && (
            <button type="button" className="upg-btn upg-btn--gold" onClick={heroAct} disabled={heroDisabled}>{heroCta}</button>
          )}
        </section>

        <footer className="upg-foot">{t('upgrade.footer')}</footer>

        {choice && opensModal(choice.kind) && (
          <PlanChangeModal
            choice={choice}
            overview={overview}
            standing={standing}
            loadPreview={loadPreview}
            onConfirm={confirm}
            onClose={() => setChoice(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="upgrade-page" data-testid="upgrade-page">
      <StreamPageHeader />
      <main className="upg-wrap">{content}</main>
      {toast && (
        <div className="upg-toast" role="status" data-testid="upgrade-toast">
          <Check size={20} />
          <span>{toast}</span>
        </div>
      )}
    </div>
  );
};

export default UpgradePage;
